import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { useEffect } from "react";

import { type GetReferralsResponse } from "../api/useGetReferrals";
import { type ReferralEligibility } from "../api/useReferralsEligibilityV2";
import { type ReferralDetails, type ReferralStatus } from "../types";
import { CannotReceiveReferralsCard } from "./ReferralStatusCards/CannotReceiveReferralsCard";
import { NoPendingReferralsCard } from "./ReferralStatusCards/NoPendingReferralsCard";
import { PaidReferralCard } from "./ReferralStatusCards/PaidReferralCard";
import { PendingReferralCard } from "./ReferralStatusCards/PendingReferralCard";
import { ReferralStatusLoadingCard } from "./ReferralStatusCards/ReferralStatusLoadingCard";
import { UnknownStatusCard } from "./ReferralStatusCards/UnknownStatusCard";

interface MyReferralsPageContentProps {
  appliedReferrals: GetReferralsResponse;
  outgoingReferralDetails: ReferralDetails;
  referralsEligibility: ReferralEligibility[];
  isLoading?: boolean;
  isError?: boolean;
  timezone: string;
}

function getReferralStatus(
  appliedReferrals: GetReferralsResponse,
  referralsEligibility: ReferralEligibility[]
): ReferralStatus {
  if (appliedReferrals.length === 0) {
    if (referralsEligibility.some((eligibility) => eligibility.attributes.eligible)) {
      return "no-pending";
    }

    return "cannot-receive";
  }

  const { bonusPaid, bonusValue, bonusExpectationMessage } = appliedReferrals[0];

  if (isDefined(bonusPaid) && bonusPaid) {
    return "paid";
  }

  if (isDefined(bonusValue) && bonusValue > 0 && bonusExpectationMessage) {
    return "pending";
  }

  return "unknown";
}

export function MyReferralsPageContent(props: MyReferralsPageContentProps) {
  const {
    appliedReferrals,
    outgoingReferralDetails,
    isLoading,
    referralsEligibility,
    isError,
    timezone,
  } = props;

  const { openArticle } = useZendeskArticle();
  const { workerReferralProgramSupportLink } = useSupportLinks();

  const status = getReferralStatus(appliedReferrals, referralsEligibility);

  useLogEffect(APP_V2_USER_EVENTS.VIEWED_MY_REFERRALS_TAB, {
    status,
    appliedReferralsCount: appliedReferrals.length,
    outgoingReferralDetails,
    referralEligibilityCount: referralsEligibility.filter(
      (eligibility) => eligibility.attributes.eligible
    ).length,
    isLoading,
    isError,
  });

  useEffect(() => {
    if (status === "unknown") {
      logError(APP_V2_USER_EVENTS.VIEWED_MY_REFERRALS_TAB_UNKNOWN_STATUS, {
        error: new Error("Unknown referral status"),
      });
    }
  }, [status]);

  return (
    <PageContent sx={{ py: 5 }}>
      <Stack spacing={3}>
        {isLoading ? (
          <ReferralStatusLoadingCard />
        ) : (
          <>
            {isError && <UnknownStatusCard />}
            {status === "no-pending" && (
              <NoPendingReferralsCard outgoingReferralDetails={outgoingReferralDetails} />
            )}
            {status === "pending" && appliedReferrals.length > 0 && (
              <PendingReferralCard referral={appliedReferrals[0]} timezone={timezone} />
            )}
            {status === "paid" && appliedReferrals.length > 0 && (
              <PaidReferralCard
                referral={appliedReferrals[0]}
                outgoingReferralDetails={outgoingReferralDetails}
              />
            )}
            {status === "cannot-receive" && (
              <CannotReceiveReferralsCard
                outgoingReferralDetails={outgoingReferralDetails}
                referralsEligibility={referralsEligibility}
              />
            )}
            {status === "unknown" && <UnknownStatusCard />}
          </>
        )}
        <Button
          variant="text"
          startIconType="help-outlined"
          size="small"
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.OPENED_MY_REFERRALS_FAQ, { status });
            await openArticle(workerReferralProgramSupportLink);
          }}
        >
          Frequently Asked Questions
        </Button>
      </Stack>
    </PageContent>
  );
}
